/**
 * SessionUnreachableError — a session check that could not reach the server: a refresh, or a validation
 * (POS-OFFLINE-STATION-LOCK-001 U15, owner ruling Q6: "Keep session, all apps").
 *
 * A session check has three outcomes, never two:
 *   - the session holds (a refresh's new access token; a validation's yes);
 *   - a refusal the server SERVED (a 4xx, or a success whose body carries no token) — the session is
 *     over, and the checker clears it;
 *   - NO ANSWER from the server — the session is not over, because nobody has refused it. The checker
 *     keeps the session and rejects with this error.
 *
 * Before it, an outage was read as the second outcome: every app signed its user out when the network
 * dropped, and a register mid-shift lost its session. A caller that sees this error keeps the session
 * and reports what really happened — the server could not be reached (`createApiClient` rejects the
 * original request as a network error; `useIdleTimeout`'s "Extend" restarts the window for the person
 * who pressed it).
 *
 * Properties:
 * - `name`     — always `'SessionUnreachableError'`
 * - `code`     — always `'SESSION_UNREACHABLE'`
 * - `endpoint` — the endpoint the check called
 * - `reason`   — `'no_answer'` (the request itself failed: offline, DNS, refused connection),
 *   `'server_failure'` (a 5xx, or a 408: the server — or a gateway in front of it — failed, and
 *   judged nothing) or `'unreadable_answer'` (a success whose body is not JSON: something other than
 *   the session route answered, e.g. a captive portal)
 * - `status`   — the status a failing server answered with; `0` when there was no answer to read
 * - `cause`    — the underlying failure, when there was one
 *
 * Imports nothing, so the token storage and the auth client can throw it without loading the HTTP client
 * (`@puffin-core/api-client/session-unreachable`).
 *
 * @module @puffin-core/api-client/errors/SessionUnreachableError
 */

/** Why the server could not answer. */
export const SESSION_UNREACHABLE_REASONS = Object.freeze({
  NO_ANSWER: 'no_answer',
  SERVER_FAILURE: 'server_failure',
  UNREADABLE_ANSWER: 'unreadable_answer',
});

const REASONS = new Set(Object.values(SESSION_UNREACHABLE_REASONS));

/**
 * @param {string} reason
 * @param {*} status
 * @returns {boolean} Whether `status` fits `reason`: a server failure names the status it answered
 *   with; the other two had no status to read.
 */
function statusFits(reason, status) {
  if (reason === SESSION_UNREACHABLE_REASONS.SERVER_FAILURE) {
    return Number.isInteger(status) && status > 0;
  }
  return status === undefined || status === 0;
}

export class SessionUnreachableError extends Error {
  /**
   * @param {Object} opts
   * @param {string} opts.endpoint - The endpoint the check called (non-empty)
   * @param {'no_answer'|'server_failure'|'unreadable_answer'} opts.reason - Why it could not answer
   * @param {number} [opts.status] - The status a failing server answered with (required for, and only
   *   for, `server_failure`)
   * @param {unknown} [opts.cause] - The underlying failure
   */
  constructor(opts) {
    if (!opts || typeof opts !== 'object') {
      throw new TypeError('SessionUnreachableError: constructor opts are required');
    }
    const { endpoint, reason, status, cause } = opts;
    if (typeof endpoint !== 'string' || endpoint.length === 0) {
      throw new TypeError('SessionUnreachableError: opts.endpoint is required');
    }
    if (!REASONS.has(reason)) {
      throw new TypeError(
        `SessionUnreachableError: opts.reason must be one of ${[...REASONS].join(', ')}`,
      );
    }
    if (!statusFits(reason, status)) {
      throw new TypeError(
        'SessionUnreachableError: opts.status is the failing answer of a server_failure, and absent otherwise',
      );
    }
    super(`Session check could not reach the server at ${endpoint} (${reason})`, { cause });
    this.name = 'SessionUnreachableError';
    this.code = 'SESSION_UNREACHABLE';
    this.endpoint = endpoint;
    this.reason = reason;
    this.status = status ?? 0;
  }
}

/** How deep a `cause` chain is followed — a real wrapper nests once or twice; a cycle stops here. */
const MAX_CAUSE_DEPTH = 8;

/**
 * Did a session check fail to reach the server? The one predicate every caller of a refresher or a validator asks
 * before it treats a failure as the end of the session — and the answer `true` means it is NOT.
 *
 * It follows the ES2022 `cause` chain, so a caller that wraps the checker's rejection is still
 * reporting an outage. It matches the class, never the `code` string: a look-alike is not one.
 *
 * @param {unknown} err
 * @returns {boolean}
 */
export function isSessionUnreachable(err) {
  let current = err;
  for (let depth = 0; depth < MAX_CAUSE_DEPTH && current; depth += 1) {
    if (current instanceof SessionUnreachableError) return true;
    current = current.cause;
  }
  return false;
}
